import React from 'react';
import {ESidePanelView, ESupplierUserPermissions} from 'constants/index';
import {reduxSetDepots, reduxToggleSidePanel} from 'appredux/actions';
import {apiGetDepots, apiGetTeam} from 'constants/Api';
import {connect} from 'react-redux';
import {ISupplierUserProfile} from 'constants/interfaces';
import AddIcon from '@material-ui/icons/Add';
import {Box, Button, Snackbar, Typography} from '@material-ui/core';
import {KhulaSupplierTable, Row} from 'components/Table/KhulaSupplierTable';
import {
  teamTableHeaders,
  teamTableRows,
} from 'screens/Dashboard/Team/TeamTableSetup';
import {PermissionsService} from 'services/PermissionsService';
import moment from 'moment/moment';
import {DepotState} from 'appredux/reducers/reducer-depots';

type TeamProps = {
  user: ISupplierUserProfile;
  depotState: DepotState;
  hideTitle?: boolean;
};

/**
 * Component used to display team members
 */
class Team extends React.Component<TeamProps> {
  state: {
    isLoading: boolean;
    team: ISupplierUserProfile[];
    isRefreshing: boolean;
    showRefreshedSnackbar: boolean;
  } = {
    isLoading: true,
    team: [],
    isRefreshing: false,
    showRefreshedSnackbar: false,
  };

  addMember() {
    const type = {
      panelType: ESidePanelView.ADD_TEAM_MEMBER,
      data: {},
    };
    reduxToggleSidePanel(true, type, false);
  }

  componentDidMount() {
    this.setState({isLoading: true}, () => {
      apiGetTeam()
        .then((team: ISupplierUserProfile[]) => {
          this.setState({team, isLoading: false});
        })
        .then(() => this.loadDepots())
        .catch((err) => {
          alert('Error loading team members');
          console.error(err);
          this.setState({team: [], isLoading: false});
        });
    });
  }

  refreshTeamMembers() {
    this.setState({isRefreshing: true}, () => {
      apiGetTeam()
        .then((team: ISupplierUserProfile[]) => {
          this.setState({
            team,
            isRefreshing: false,
            showRefreshedSnackbar: true,
          });
        })
        .then(() => this.loadDepots())
        .catch((err) => {
          alert('Failed to load team members. Please try again later');
          console.error(err);
          this.setState({team: [], isRefreshing: false});
        });
    });
  }

  // todo extract depot service for loading depots and handling stale data
  loadDepots(): Promise<void> {
    const {user, depotState} = this.props;
    const {lastUpdate, depots} = depotState;

    const depotsAreExpired = moment(lastUpdate).isBefore(
      moment().subtract(24, 'hours'),
    );
    const noDepots = !depots?.length;

    if (depotsAreExpired || noDepots)
      return apiGetDepots(user)
        .then((depots) => reduxSetDepots(depots))
        .then(() => {});

    return Promise.resolve();
  }

  editTeamMember(teamMember: ISupplierUserProfile) {
    const type = {
      panelType: ESidePanelView.EDIT_TEAM_MEMBER,
      data: teamMember,
    };
    reduxToggleSidePanel(true, type, false);
  }

  editPermissions(teamMember: ISupplierUserProfile) {
    const type = {
      panelType: ESidePanelView.EDIT_TEAM_MEMBER_PERMISSIONS,
      data: teamMember,
    };
    reduxToggleSidePanel(true, type, false);
  }

  removeTeamMember(teamMember: ISupplierUserProfile) {
    const type = {
      panelType: ESidePanelView.REMOVE_TEAM_MEMBER,
      data: teamMember,
    };
    reduxToggleSidePanel(true, type, false);
  }

  shouldShowDepotColumn() {
    const {team} = this.state;
    const teamDepotIds = team.map((teamMember) => teamMember.depot?.id);
    const uniqueTeamDepots = Array.from(new Set(teamDepotIds));
    return uniqueTeamDepots.length > 1;
  }

  render() {
    const {team, isLoading, isRefreshing, showRefreshedSnackbar} = this.state;

    const mapRowToTeamMember = (row: Row) =>
      // @ts-ignore
      team.find((teamMember) => teamMember.id == row.id);

    const shouldShowDepotColumn = this.shouldShowDepotColumn();

    const permissions = new PermissionsService(this.props.user);
    const canWrite = permissions.hasPermission(
      ESupplierUserPermissions.TEAM_WRITE,
    );

    return (
      <div className="table-screen teams-container">
        {!this.props.hideTitle && (
          <Typography variant="h5" className="page-title">
            My Team
          </Typography>
        )}
        <Box className="actions-row">
          {canWrite && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => this.addMember()}
              size="small"
              color={'primary'}
            >
              Add Team Member
            </Button>
          )}
        </Box>
        <div className="orders-section">
          <KhulaSupplierTable
            headers={teamTableHeaders(shouldShowDepotColumn)}
            rows={teamTableRows(
              team,
              (teamMember) => this.editTeamMember(teamMember),
              (teamMember) => this.editPermissions(teamMember),
              (teamMember) => this.removeTeamMember(teamMember),
            )}
            defaultSortField={'name'}
            filtersGroups={[]}
            isLoading={isLoading}
            isRefreshing={isRefreshing}
            onRefresh={() => this.refreshTeamMembers()}
            onRowSelect={(row: any) => {
              const teamMember = mapRowToTeamMember(row);
              if (!teamMember) return;
              this.editTeamMember(teamMember);
            }}
          />
          <Snackbar
            anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            open={showRefreshedSnackbar}
            onClose={() => this.setState({showRefreshedSnackbar: false})}
            autoHideDuration={3000}
            message="Team refreshed"
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: any) => {
  return {
    user: state.user,
    depotState: state.depots,
  };
};

export default connect(mapStateToProps)(Team);
